import { authenticatedInstance } from '../index/index.service';
import type { OrderlineView } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { OrderlineView } from 'shared-models/browser';

export type TransferOrderlineMove = {
  orderlineId: number;
  quantity: number;
};

export type TransferableOrderlinesResult = {
  orderlines: OrderlineView[];
  remainingToPay: number;
};

class TransferService {
  // Summary: Lists non-skipped orderlines plus the order's remaining-to-pay ceiling for the transfer item-select step.
  // Details: docs/function-library/web/services.md#transfer-service
  async listTransferableOrderlines(
    tenantId: string,
    orderId: number,
    employeeSession: string,
  ): Promise<TransferableOrderlinesResult> {
    try {
      const response = await authenticatedInstance.get<TransferableOrderlinesResult>(
        `/transfer/tenant/${tenantId}/order/${orderId}/orderlines`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading transferable orderlines'));
    }
  }

  // Summary: Moves one or many orderlines from sourceOrderId to targetOrderId. 204 response; changes propagate via SSE.
  // Details: docs/function-library/web/services.md#transfer-service
  async transferOrderlines(
    tenantId: string,
    sourceOrderId: number,
    targetOrderId: number,
    orderlineMoves: TransferOrderlineMove[],
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/transfer/tenant/${tenantId}/order/${sourceOrderId}/orderlines`,
        { targetOrderId, orderlineMoves },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Transfer failed'));
    }
  }

  // Summary: Reassigns an order's table to a different table (full-table transfer). 204 response.
  // Details: docs/function-library/web/services.md#transfer-service
  async transferOrderToTable(
    tenantId: string,
    orderId: number,
    targetTableId: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/transfer/tenant/${tenantId}/order/${orderId}/table`,
        { targetTableId },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Transfer failed'));
    }
  }
}

export const transferService = new TransferService();
